/**
 * Уведомление, которое ушло автоматически по сделке чата в группу салона, — плашкой в ленте «Чатов».
 *
 * Просьба владельца от 23.10.26: «в режиме чата сделай вывод, какие уведомления срабатывали
 * автоматически, в том числе в группу салона… кликнуть и перейти в группу и прочитать хронологию от
 * этого сообщения». Вид — по центру ленты, как комментарий CRM, но со своей рамкой и колокольчиком:
 * клиент его не видел, это наше сообщение сотрудникам. Шапка — что и куда ушло, стадия цепочки;
 * ниже — последний ответ руководителя; «Открыть в группе» — чат группы на этом сообщении (`?msg=`).
 * Данные — `notices` ручек `GET /api/chats/{id}` и `…/history` (`chat/notices.py`).
 */
import { BellRing, CornerDownRight } from "lucide-react";
import { Link } from "react-router";

import type { ChatNotice } from "@/lib/clientChats";

const hhmm = (iso: string) => iso.slice(11, 16);
const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

const STATE_CLS: Record<ChatNotice["state"], string> = {
  sent: "bg-warn-bg text-warn",
  answered: "bg-accent/10 text-accent-text",
  returned: "bg-ok-bg text-ok",
  failed: "bg-err-bg text-err",
};

export function NoticeBubble({ n }: { n: ChatNotice }) {
  const href = n.group_chat_id ? `/chats/${n.group_chat_id}${n.message_id ? `?msg=${n.message_id}` : ""}` : null;
  return (
    <div className="mx-auto my-1 w-full max-w-[460px] rounded-[10px] border border-dashed border-accent/60 bg-accent/5 px-3 py-2 text-[12.5px]" data-annot={`уведомление ${n.kind} ${n.id}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-fg-muted">
        <BellRing size={13} className="text-accent-text" aria-hidden />
        <span className="font-medium text-fg">Уведомление в группу «{n.group}»</span>
        <span>· клиент не видит</span>
        <span className="ml-auto tabular-nums">{hhmm(n.date)}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>{n.title}</span>
        <span className="text-fg-muted">сделка {n.deal_id}</span>
        <span className={`rounded px-1.5 py-0.5 text-[11px] ${STATE_CLS[n.state]}`}>
          {n.state_label}
          {n.returned ? ` ${ddmm(n.returned)}` : ""}
        </span>
        {n.asks > 0 && <span className="text-[11.5px] text-fg-muted">переспрашивали {n.asks}</span>}
        {n.test && <span className="text-[11.5px] text-fg-faint">проба</span>}
      </div>
      {n.reply ? (
        <div className="mt-1 flex gap-1 text-[12px]">
          <CornerDownRight size={13} className="mt-0.5 shrink-0 text-fg-faint" aria-hidden />
          <span>
            <span className="text-fg-muted">
              {n.reply.name} · {ddmm(n.reply.at)} {hhmm(n.reply.at)}
              {n.answers > 1 ? ` · ответов ${n.answers}` : ""}:
            </span>{" "}
            <span className="line-clamp-3 whitespace-pre-wrap">{n.reply.text || "фото"}</span>
          </span>
        </div>
      ) : (
        n.state !== "returned" && <div className="mt-1 text-[12px] text-fg-muted">Ответа из салона пока нет.</div>
      )}
      {href ? (
        <Link to={href} className="mt-1 inline-block text-[12px] text-accent-text hover:underline" title="Чат группы салона на этом сообщении: ответы и переспросы — веткой">
          Открыть в группе →
        </Link>
      ) : (
        <div className="mt-1 text-[11.5px] text-fg-faint">Группы нет в «Чатах» — переписку смотрите в Telegram.</div>
      )}
    </div>
  );
}
